import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from 'oidc-react';
import { AuthUser } from '@/types/auth';
import { apiClient } from '@/services/api';
import { useApiClient } from '@/hooks/useApiClient';

interface UseUserResult {
  user: AuthUser | undefined;
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
}

/**
 * Hook to fetch and access full user profile from backend /auth/me endpoint
 *
 * This hook replaces direct access to userData.profile from OIDC, which only
 * contains minimal token claims. The backend /auth/me endpoint returns the
 * complete user profile including email, agronodKonton, and other fields.
 *
 * @returns User data with loading/error states and authentication status
 *
 * @example
 * ```tsx
 * const { user, isLoading, isAuthenticated } = useUser();
 *
 * if (!isAuthenticated) return <LoginPrompt />;
 * if (isLoading) return <Loading />;
 *
 * // Use type guards for safe access
 * if (isLantbrukareUser(user)) {
 *   console.log(user.email);
 *   console.log(getFarmName(user));
 * }
 * ```
 */
export const useUser = (): UseUserResult => {
  const { userData, isLoading: oidcLoading } = useAuth();
  // Ensure token is set on apiClient BEFORE queries fire
  useApiClient();
  const accessToken = userData?.access_token;
  const isAuthenticated = !!accessToken;

  const {
    data: user,
    isLoading: queryLoading,
    error
  } = useQuery<AuthUser>({
    queryKey: ['user', 'me', accessToken], // Include token in key to refetch on token change
    queryFn: async () => {
      const response = await apiClient.get<AuthUser>('/auth/me');
      return response;
    },
    enabled: isAuthenticated && !oidcLoading, // Wait for OIDC to complete AND have token
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Retry once on failure
    placeholderData: keepPreviousData, // Keep previous data during token refresh to prevent loading flash
  });

  return {
    user,
    isLoading: oidcLoading || queryLoading,
    error: error as Error | null,
    isAuthenticated,
  };
};
